// Bancos que o "Limpar dados" NÃO conseguiu apagar — e o que o app faz com isso.
//
// ── Por que existe ──
// O "Limpar dados" apaga os cinco bancos e troca o documento. Quando um banco
// não sai (delete recusado, ou pendurado além do prazo — um delete bloqueado não
// rejeita, pendura), o handler só podia dar `console.warn`, que ninguém vê numa
// TV: o app recomeçava dizendo, por omissão, "está tudo limpo". É a escotilha
// que promete mais do que alcança.
//
// ── O que acontece agora ──
//  1. O handler REGISTRA os que ficaram (depois do `localStorage.clear()`, senão
//     o próprio clear apagaria o registro).
//  2. O boot seguinte TENTA DE NOVO, dentro do `bootstrap`, ANTES do render: é o
//     único momento em que nenhum módulo tem conexão aberta, então o delete não
//     tem com quem bloquear. Na prática é o que torna a promessa verdadeira sem o
//     usuário precisar fazer nada.
//  3. Se nem assim sair, o registro fica, a tentativa se repete em todo boot, e a
//     tela de Configurações DIZ isso (na parte pública da seção Conta, visível
//     sem login — o botão de "Apagar tudo" fica atrás do login, e quem acabou de
//     limpar está deslogado).
//
// Nunca lança: o boot não pode cair por causa disto.
//
// COMPAT Chr38: só JSON, try/catch, Array.

import { destroyDbsByName } from './idb-opener'

const STORAGE_KEY = 'swipe:limpar-dados-pendente:v1'

/** Prazo da nova tentativa no boot. Mesmo teto do "Limpar dados" (idb-opener,
 *  DESTROY_ALL_TIMEOUT_MS), pela mesma razão: delete pendurado não pode segurar
 *  o app. Só roda quando há registro — o boot normal não paga nada. */
export const RETRY_TIMEOUT_MS = 3_000

function lerLista(): string[] {
  try {
    if (typeof localStorage === 'undefined') return []
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    const nomes = parsed && typeof parsed === 'object' ? (parsed as { nomes?: unknown }).nomes : null
    if (!Array.isArray(nomes)) return []
    return nomes.filter((n): n is string => typeof n === 'string' && n.length > 0)
  } catch {
    return []
  }
}

function gravarLista(nomes: readonly string[]): boolean {
  try {
    if (typeof localStorage === 'undefined') return false
    if (nomes.length === 0) {
      localStorage.removeItem(STORAGE_KEY)
      return true
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ nomes, desde: Date.now() }))
    return true
  } catch {
    return false
  }
}

/** Os bancos que ainda não saíram. Lista vazia = nada pendente. */
export function limparDadosPendentes(): string[] {
  return lerLista()
}

/**
 * O handler do "Limpar dados" chama isto DEPOIS do `localStorage.clear()`.
 * Lista vazia = "nada pendente": remove o registro (não grava lista vazia).
 */
export function registrarLimparDadosPendente(nomes: readonly string[]): boolean {
  return gravarLista(nomes)
}

/**
 * Nova tentativa no boot. Apaga os pendentes, regrava só os que AINDA ficaram
 * (ou remove o registro se saíram todos) e devolve os que ficaram.
 *
 * Tem que rodar ANTES de qualquer módulo abrir um banco — é o que garante que
 * o delete não bloqueie. Hoje isso é o `bootstrap`, antes do `render`.
 */
export async function concluirLimparDadosPendente(
  opts: { timeoutMs?: number } = {},
): Promise<string[]> {
  const pendentes = lerLista()
  if (pendentes.length === 0) return []
  let restantes: string[]
  try {
    const r = await destroyDbsByName(pendentes, { timeoutMs: opts.timeoutMs ?? RETRY_TIMEOUT_MS })
    restantes = r.failed
  } catch {
    restantes = pendentes
  }
  gravarLista(restantes)
  return restantes
}
